/* eslint import/no-webpack-loader-syntax: off */
// TODO: Look at putting this loader into my webpack.config.js file
import { useRef, useEffect } from "react";
import "./Map.css";
import { useDispatch, useSelector } from "react-redux";
import {
  updateLocation,
  selectLocation,
} from "../StateManagement/currentLocationSlice";
import MapLegendMobile from "../MapLegend/MapLegendMobile";
import MapLegend from "../MapLegend/MapLegend";
import useLocationList from "../Clients/useLocationList";
import { isMobile } from "../StateManagement/getMobileSite";

import mapboxgl from "!mapbox-gl";
import { selectStatistic } from "../StateManagement/statisticSourceSlice";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";

import locationList from "../Data/US_CostLiving_LocationList.json";

mapboxgl.accessToken = process.env.REACT_APP_MAP_BOX_TOKEN;

const Map3D = () => {
  const mapContainer = useRef(null);
  const map = useRef(null);

  const dispatch = useDispatch();

  const location = useSelector(selectLocation);
  const statistic = useSelector(selectStatistic);
  const [locations] = useLocationList();

  // use Effect for creating the map
  useEffect(() => {
    if (!locations) return;

    // Due to react strict mode, this will be initialized twice. To prevent side affects, remove current map before initializing.
    if (map.current) {
      map.current.remove();
      map.current = null;
    }

    const mapProperties = {
      container: mapContainer.current,
      style: "mapbox://styles/cwhitman/cmd7wzgri002s01qsemvgd2uz",
      antialias: true,
      zoom: isMobile() ? 2 : 3.7,
      center: [-97.5795, 39.8283],
      pitch: 75,
      projection: 'mercator',
      failIfMajorPerformanceCaveat: false, // Ensure WebGL 2 support
    };

    map.current = new mapboxgl.Map(mapProperties);

    const modelAltitude = 0;

    const mapCenterCoords = mapboxgl.MercatorCoordinate.fromLngLat(
      map.current.getCenter(),
      modelAltitude
    );

    const modelTransform = {
      translateX: mapCenterCoords.x,
      translateY: mapCenterCoords.y,
      translateZ: mapCenterCoords.z,
      scale: mapCenterCoords.meterInMercatorCoordinateUnits()
    };

    const sceneTranslation = new THREE.Matrix4()
      .makeTranslation(
        modelTransform.translateX,
        modelTransform.translateY,
        modelTransform.translateZ
      );

    // Use PerspectiveCamera for raycasting
    const camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1e6
    );
    const scene = new THREE.Scene();

    const createCustomLayer = (map) => {
      const directionalLight1 = new THREE.DirectionalLight(0xffffff);
      directionalLight1.position.set(0, -70, 100).normalize();
      scene.add(directionalLight1);

      const directionalLight2 = new THREE.DirectionalLight(0xffffff);
      directionalLight2.position.set(0, 70, 100).normalize();
      scene.add(directionalLight2);

      const loader = new GLTFLoader();
      const overallScale = 6;
      const heightScale = 5;

      loader.load(
        '/building.glb',
        (gltf) => {
          locationList.sort(function (a, b) {
            return a.latitude - b.longitude
          })
          for (let i = 0; i < locationList.length; i++) {
            let location = locationList[i]
            const modelClone = gltf.scene.clone();
            modelClone.userData.location = location; // Attach location data

            modelClone.traverse((child) => {
              // Set Color of buildings exterior
              if (child.isMesh && child.name === "Plane025_1") {
                child.material = new THREE.MeshStandardMaterial({
                  color: new THREE.Color(location.colors['rent']),
                  emissive: new THREE.Color(location.colors['rent']),
                  roughness: 0.4,
                  normalScale: { x: 1, y: -1 },
                  side: 2

                });
              }

              // Set color building windows
              if (child.isMesh && child.name === "Plane025") {
                child.material = new THREE.MeshStandardMaterial({
                  color: new THREE.Color("#04cbd1"),
                  emissive: new THREE.Color("#04cbd1"),
                  roughness: 0.4,
                  normalScale: { x: 1, y: -1 },
                  side: 2

                });
              }

            });

            const modelCoords = mapboxgl.MercatorCoordinate.fromLngLat(
              [location.longitude, location.latitude],
              0
            );

            const normalizedStatistic = locations.getNormalizedLocationStat(location, statistic.statistic);
            modelClone.scale.set(overallScale * modelTransform.scale,
              -overallScale * normalizedStatistic * heightScale * modelTransform.scale,
              overallScale * modelTransform.scale);

            // Compute bounding box and height
            const box = new THREE.Box3().setFromObject(modelClone);
            const height = box.max.y - box.min.y;

            // Offset so the base sits on the ground
            const verticalOffset = height / 2; // Adjust to height if needed
            modelClone.position.set(modelCoords.x - mapCenterCoords.x,
              modelCoords.y - mapCenterCoords.y,
              verticalOffset);
            modelClone.quaternion.setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0, "XYZ")); // Random Y rotation

            scene.add(modelClone);
          }
        }
      );

      const renderer = new THREE.WebGLRenderer({
        canvas: map.getCanvas(),
        context: map.painter.context.gl,
        antialias: true
      });

      renderer.autoClear = false;

      return {
        id: 'buildings',
        type: 'custom',
        renderingMode: '3d',
        onAdd: () => {
          // Add logic that runs on layer addition if necessary.
        },
        render: (gl, matrix) => {
          const m = new THREE.Matrix4().fromArray(matrix);
          camera.projectionMatrix = m.multiply(sceneTranslation);

          renderer.resetState();
          renderer.render(scene, camera);
          map.triggerRepaint();
        }
      };
    };

    map.current.on('style.load', () => {
      const customLayer = createCustomLayer(map.current);
      map.current.addLayer(customLayer);
    });

    map.current.on("click", (event) => {
      // Convert browser pixel coords → Normalized Device Co‑ordinates (NDC)
      const mouse = new THREE.Vector2(
        (event.point.x / map.current.transform.width) * 2 - 1,
        -(event.point.y / map.current.transform.height) * 2 + 1
      );

      // Build the picking ray
      const raycaster = new THREE.Raycaster();
      const camInvProj = camera.projectionMatrix.clone().invert();
      const cameraPos = new THREE.Vector3().applyMatrix4(camInvProj);
      const mousePos = new THREE.Vector3(mouse.x, mouse.y, 1).applyMatrix4(camInvProj);
      const viewDir = mousePos.sub(cameraPos).normalize();

      raycaster.set(cameraPos, viewDir);
      const intersects = raycaster.intersectObjects(scene.children, /*recursive=*/true);

      if (intersects.length) {
        // Climb up the hierarchy until we find the building root that carries the location
        let hit = intersects[0].object;
        while (hit && !hit.userData.location) hit = hit.parent;

        if (hit?.userData.location) {
          dispatch(updateLocation(hit.userData.location));
          return;
        } 
      }else{
          dispatch(updateLocation(null));
        }
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations, statistic, dispatch]);

  //Use Effect for updating map
  useEffect(() => {
    if (!map || !map.current) return;

    if (location.current && locations) {
      const locationProps = locations.getLocation(location.current.id);
      map.current.setCenter([locationProps.longitude, locationProps.latitude]);
      
     var normalizedStatistic = locations.getNormalizedLocationStat(locationProps, statistic.statistic);
     map.current.setZoom(Math.min(Math.max(4, 3 + (1-normalizedStatistic)*4), 8));
    }
  }, [location, locations, statistic]);

  return (
    <div
      key={statistic.statistic}
      ref={mapContainer}
      style={{
        width: "inherit",
        height: "inherit",
        position: "absolute",
        padding: 0,
      }}
    >
      {isMobile() ? (
        <div style={{ position: "absolute", top: "60%", left: "2%" }}>
          <MapLegendMobile></MapLegendMobile>
        </div>
      ) : (
        <div style={{ position: "absolute", top: "55%", left: "5%" }}>
          <MapLegend></MapLegend>
        </div>
      )}
    </div>
  );
};

Map3D.propTypes = {};

Map3D.defaultProps = {};

export default Map3D;
